import React, { memo, useContext } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { GlobalRightsContext } from 'providers/GlobalRightsContext';
import { Grid, Popover } from '@mui/material';
import { IconButton, Typography } from '@totalsoft/rocket-ui';
import {
  Print,
  AccountTree,
  FileCopy,
  ArrowBack,
  EuroSymbol,
  Timeline,
  Assignment,
  List,
  Remove,
  Folder,
  DoneAll,
  CompareArrows,
  People,
  Check,
  AssignmentLate,
  PlaylistAddCheck
} from '@mui/icons-material';
import OfferStatesSelectionListContainer from './states/OfferStatesSelectionListContainer';
import { emptyString } from 'constants/emptyConst';

const OfferHeader = ({
  t,
  data,
  onChangeActiveStep,
  scheduleRead,
  viewFolder,
  viewWorkflow,
  offerWithChecklist,
  clientVerificationsRead,
  offerClausesRead,
  clientRelationsRead,
  documentChecklistRead,
  offerCompetenceorderRead,
  documentReportsRead,
  documentPropertiesRead,
  otherStateTransitionHistoryRead,
  enablePartnerRelations,
  documentId,
  siteId,
  history,
  onChangeShowReportsModal,
  loadOfferReports,
  onChangeOpenDuplicateConfirmBool,
  isImage,
  openOfferActions,
  handleOpenOfferActions,
  handleCloseOfferActions,
  anchorEl,
  isFacilityFrame,
  isContractModification,
  isFinancingOffer
}) => {
  const defaultGridActions = 3;
  const defaultGridSpace = 3;
  const { addNewOffer, addFacilityOffer } = useContext(GlobalRightsContext);

  const offerIsInRightState = data.stateId !== data.draft;

  const onDuplicateClick = () => {
    onChangeOpenDuplicateConfirmBool(true);
  };

  const onReportsClick = () => {
    onChangeShowReportsModal(true);
    loadOfferReports(documentId, siteId);
  };

  const onPrintClick = () => {
    onChangeActiveStep(-1);
    history.push(`/offers/${documentId}/${siteId}/documents`);
  };

  const onVerificationsClick = () => {
    onChangeActiveStep(-1);
    history.push(`/offers/${documentId}/${siteId}/verifications`);
  };

  const onCloseClick = () => {
    const urlFilters = sessionStorage.getItem('OfferURLFilters');
    sessionStorage.removeItem('OfferURLFilters');
    history.push('/offers' + (urlFilters ?? emptyString));
  };

  const onPropertiesClick = () => {
    onChangeActiveStep(-1);
    history.push(`/offers/${documentId}/${siteId}/document-properties`);
  };

  const onPartnerRelationsClick = () => {
    onChangeActiveStep(-1);
    history.push(`/offers/${documentId}/${siteId}/partnerRelations`);
  };

  const onStateTransitionsClick = () => {
    onChangeActiveStep(-1);
    history.push(`/offers/${documentId}/${siteId}/state-transitions`);
  };

  const onCheckListClick = () => {
    onChangeActiveStep(-1);
    history.push(`/offers/${documentId}/${siteId}/checklist`);
  };

  const onPaymentPlanClick = () => {
    onChangeActiveStep(-1);
    history.push({
      pathname: `/offers/${documentId}/${siteId}/paymentPlan`,
      state: { fromAction: true }
    });
  };

  const onWorkflowClick = () => {
    onChangeActiveStep(-1);
    history.push(`/offers/${documentId}/${siteId}/workflows`);
  };

  const onOpenFolderClick = () => {
    onChangeActiveStep(-1);
    history.push(`/folders/${data.folderId}/${siteId}`);
  };

  const onCompetenceOrderClick = () => {
    onChangeActiveStep(-1);
    history.push(`/offers/${documentId}/${siteId}/competence-order`);
  };

  const onOfferConditionsClick = () => {
    onChangeActiveStep(-1);
    history.push(`/offers/${documentId}/${siteId}/offer-conditions`);
  };

  const getNumberOfGridActions = () => {
    return (
      defaultGridActions +
      !isImage +
      offerWithChecklist +
      scheduleRead +
      viewWorkflow +
      viewFolder +
      clientVerificationsRead +
      offerIsInRightState
    );
  };

  return (
    <Grid container alignItems='center' justifyContent='space-between'>
      {isImage ? (
        <Typography fontWeight='bold' variant='subtitle1'>
          {isFacilityFrame
            ? t('FacilityFrameEdit.ImagePageTitle', {
                data: {
                  ...data,
                  offerDate: t('DATE_FORMAT', { date: { value: data.offerDate, format: 'DD/MM/YYYY' } })
                }
              })
            : t('OfferEdit.ImagePageTitle', {
                data: {
                  ...data,
                  offerDate: t('DATE_FORMAT', { date: { value: data.offerDate, format: 'DD/MM/YYYY' } })
                }
              })}
        </Typography>
      ) : (
        <Grid item xs alignItems='center' display='flex' gap={2} flexShrink={1} minWidth={0}>
          <Typography fontWeight='bold' variant='subtitle1' noWrap>
            {isFacilityFrame
              ? t('FacilityFrameEdit.PageTitle', { data: data })
              : t('OfferEdit.PageTitle', {
                  offerNumber: data.offerNumber,
                  clientName: data.client.clientName ? `, ${data.client.clientName}` : ''
                })}
          </Typography>
          <OfferStatesSelectionListContainer
            documentId={documentId}
            siteId={siteId}
            folderSelectionDone={data.folderSelectionDone}
            folderList={data.folderList}
            processing={data.processing}
          />
        </Grid>
      )}
      <Grid item container xs='auto' justifyContent='end'>
        <IconButton
          onClick={onCloseClick}
          tooltip={t('OfferAction.BackToList')}
          color='primary'
          variant='text'
          id={'offerHeader_action_backToList'}
        >
          <ArrowBack />
        </IconButton>
        {!isImage && (
          <IconButton
            tooltip={t('OfferAction.Print')}
            color='primary'
            variant='text'
            disabled={data.processing}
            onClick={onPrintClick}
            id={'offerHeader_action_print'}
          >
            <Print />
          </IconButton>
        )}

        <Popover
          open={openOfferActions}
          anchorEl={anchorEl}
          onClose={handleCloseOfferActions}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
        >
          <Grid container py={1}>
            {documentReportsRead && (
              <Grid
                item
                textAlign='center'
                xs={getNumberOfGridActions() > defaultGridActions ? defaultGridSpace : 12 / getNumberOfGridActions()}
              >
                <IconButton
                  color='primary'
                  variant='text'
                  tooltip={t('OfferAction.Reports')}
                  disabled={data.processing}
                  onClick={onReportsClick}
                  id={'offerHeader_actionList_reports'}
                >
                  <Assignment />
                </IconButton>
              </Grid>
            )}
            {clientRelationsRead && enablePartnerRelations && (
              <Grid
                item
                textAlign='center'
                xs={getNumberOfGridActions() > defaultGridActions ? defaultGridSpace : 12 / getNumberOfGridActions()}
              >
                <IconButton
                  color='primary'
                  variant='text'
                  tooltip={t('OfferAction.PartnerRelations')}
                  onClick={onPartnerRelationsClick}
                  id={'offerHeader_actionList_partnerRelations'}
                  disabled={data.processing}
                >
                  <People />
                </IconButton>
              </Grid>
            )}
            {otherStateTransitionHistoryRead && (
              <Grid
                item
                textAlign='center'
                xs={getNumberOfGridActions() > defaultGridActions ? defaultGridSpace : 12 / getNumberOfGridActions()}
              >
                <IconButton
                  color='primary'
                  variant='text'
                  tooltip={t('OfferAction.StateTransitions')}
                  onClick={onStateTransitionsClick}
                  disabled={data.processing}
                  id={'offerHeader_actionList_stateTransitions'}
                >
                  <CompareArrows />
                </IconButton>
              </Grid>
            )}
            {offerWithChecklist && documentChecklistRead && (
              <Grid
                item
                textAlign='center'
                xs={getNumberOfGridActions() > defaultGridActions ? defaultGridSpace : 12 / getNumberOfGridActions()}
              >
                <IconButton
                  color='primary'
                  variant='text'
                  tooltip={t('OfferAction.CheckList')}
                  onClick={onCheckListClick}
                  disabled={data.processing}
                  id={'offerHeader_actionList_checklist'}
                >
                  <PlaylistAddCheck />
                </IconButton>
              </Grid>
            )}
            {!isImage && !isContractModification && (
              <Grid
                item
                textAlign='center'
                xs={getNumberOfGridActions() > defaultGridActions ? defaultGridSpace : 12 / getNumberOfGridActions()}
              >
                <IconButton
                  color='primary'
                  variant='text'
                  tooltip={t('OfferAction.Duplicate')}
                  onClick={onDuplicateClick}
                  disabled={
                    data.processing || !data.hasActiveAsset || (isFinancingOffer && !addNewOffer) || (isFacilityFrame && !addFacilityOffer)
                  }
                  id={'offerHeader_actionList_duplicate'}
                >
                  <FileCopy />
                </IconButton>
              </Grid>
            )}
            {documentPropertiesRead && (
              <Grid
                item
                textAlign='center'
                xs={getNumberOfGridActions() > defaultGridActions ? defaultGridSpace : 12 / getNumberOfGridActions()}
              >
                <IconButton
                  color='primary'
                  variant='text'
                  tooltip={t('OfferAction.Properties')}
                  onClick={onPropertiesClick}
                  disabled={data.processing}
                  id={'offerHeader_actionList_properties'}
                >
                  <AccountTree />
                </IconButton>
              </Grid>
            )}
            {scheduleRead && !isFacilityFrame && (
              <Grid
                item
                textAlign='center'
                xs={getNumberOfGridActions() > defaultGridActions ? defaultGridSpace : 12 / getNumberOfGridActions()}
              >
                <IconButton
                  color='primary'
                  variant='text'
                  tooltip={t('OfferAction.PaymentPlan')}
                  onClick={onPaymentPlanClick}
                  disabled={data.processing}
                  id={'offerHeader_actionList_paymentPlan'}
                >
                  <EuroSymbol />
                </IconButton>
              </Grid>
            )}
            {viewWorkflow && !isImage && (
              <Grid
                item
                textAlign='center'
                xs={getNumberOfGridActions() > defaultGridActions ? defaultGridSpace : 12 / getNumberOfGridActions()}
              >
                <IconButton
                  color='primary'
                  variant='text'
                  tooltip={t('OfferAction.Workflow')}
                  onClick={onWorkflowClick}
                  disabled={data.processing}
                  id={'offerHeader_actionList_workflow'}
                >
                  <Timeline />
                </IconButton>
              </Grid>
            )}
            {clientVerificationsRead && offerIsInRightState && (
              <Grid
                item
                textAlign='center'
                xs={getNumberOfGridActions() > defaultGridActions ? defaultGridSpace : 12 / getNumberOfGridActions()}
              >
                <IconButton
                  color='primary'
                  variant='text'
                  tooltip={t('OfferAction.Verification')}
                  onClick={onVerificationsClick}
                  disabled={data.processing}
                  id={'offerHeader_actionList_verifications'}
                >
                  <DoneAll />
                </IconButton>
              </Grid>
            )}
            {viewFolder && (
              <Grid
                item
                textAlign='center'
                xs={getNumberOfGridActions() > defaultGridActions ? defaultGridSpace : 12 / getNumberOfGridActions()}
              >
                <IconButton
                  color='primary'
                  variant='text'
                  tooltip={t('OfferAction.OpenFolder')}
                  onClick={onOpenFolderClick}
                  id={'offerHeader_actionList_openFolder'}
                  disabled={!data.folderId || data.processing}
                >
                  <Folder />
                </IconButton>
              </Grid>
            )}
            {data.hasAnyCompetenceOrder && offerCompetenceorderRead && (
              <Grid
                item
                textAlign='center'
                xs={getNumberOfGridActions() > defaultGridActions ? defaultGridSpace : 12 / getNumberOfGridActions()}
              >
                <IconButton
                  color='primary'
                  variant='text'
                  tooltip={t('OfferAction.CompetenceOrder')}
                  onClick={onCompetenceOrderClick}
                  disabled={data.processing}
                  id={'offerHeader_actionList_competenceOrder'}
                >
                  <Check />
                </IconButton>
              </Grid>
            )}

            {offerClausesRead && (
              <Grid
                item
                textAlign='center'
                xs={getNumberOfGridActions() > defaultGridActions ? defaultGridSpace : 12 / getNumberOfGridActions()}
              >
                <IconButton
                  color='primary'
                  variant='text'
                  tooltip={t('OfferAction.OfferConditions')}
                  onClick={onOfferConditionsClick}
                  disabled={data.processing}
                  id={'offerHeader_actionList_offerConditions'}
                >
                  <AssignmentLate />
                </IconButton>
              </Grid>
            )}
          </Grid>
        </Popover>
        {!openOfferActions ? (
          <IconButton
            color='primary'
            variant='text'
            tooltip={t('Tooltips.ShowHeaderActions')}
            onClick={handleOpenOfferActions}
            id={'offerHeader_actionList_showBtn'}
          >
            <List />
          </IconButton>
        ) : (
          <IconButton
            color='primary'
            variant='text'
            tooltip={t('Tooltips.HideHeaderActions')}
            onClick={handleCloseOfferActions}
            id={'offerHeader_actionList_hideBtn'}
          >
            <Remove />
          </IconButton>
        )}
      </Grid>
    </Grid>
  );
};

OfferHeader.propTypes = {
  data: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  onChangeActiveStep: PropTypes.func.isRequired,
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  history: PropTypes.object.isRequired,
  onChangeShowReportsModal: PropTypes.func.isRequired,
  loadOfferReports: PropTypes.func.isRequired,
  onChangeOpenDuplicateConfirmBool: PropTypes.func.isRequired,
  scheduleRead: PropTypes.bool,
  viewFolder: PropTypes.bool,
  offerWithChecklist: PropTypes.bool.isRequired,
  clientVerificationsRead: PropTypes.bool,
  offerClausesRead: PropTypes.bool,
  clientRelationsRead: PropTypes.bool,
  documentChecklistRead: PropTypes.bool,
  offerCompetenceorderRead: PropTypes.bool,
  documentReportsRead: PropTypes.bool,
  documentPropertiesRead: PropTypes.bool,
  otherStateTransitionHistoryRead: PropTypes.bool,
  enablePartnerRelations: PropTypes.bool,
  isImage: PropTypes.bool,
  openOfferActions: PropTypes.bool,
  handleOpenOfferActions: PropTypes.func,
  handleCloseOfferActions: PropTypes.func,
  viewWorkflow: PropTypes.bool,
  anchorEl: PropTypes.object,
  isFacilityFrame: PropTypes.bool,
  isContractModification: PropTypes.bool,
  isFinancingOffer: PropTypes.bool
};

export default compose(withTranslation())(memo(OfferHeader));
